import { Children, PropsWithChildren, ReactNode } from 'react';
import {
  Table as MuiTable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Tooltip,
  Stack,
  Typography,
} from '@mui/material';

import { getFragmentData } from '@api/gql';
import { MaterialIcon, NoResults, TableWrapper } from '@components';
import { StandingAccessReportEntitlementFragmentFragment } from '@api/gql/graphql';
import { RiskScoreItem } from '@common/components/RiskScore';
import PrimarySecondary from '@common/ui/PrimarySecondary';

import {
  standingAccessReportPermissionFragmentDocument,
  standingAccessReportResourceFragmentDocument,
} from '@StandingAccess/services/gql';
import AssessmentReportLastUsed from './AssessmentReportLastUsed';

export default function AssessmentReportTable({
  children,
  isLoading,
  remediationTitle = 'Remediation Progress',
}: PropsWithChildren<{ isLoading?: boolean; remediationTitle?: string }>) {
  if (Children.count(children) === 0) return <NoResults>No entitlements found</NoResults>;

  return (
    <TableWrapper loading={isLoading} noBackground>
      <MuiTable>
        <TableHead>
          <TableRow>
            <TableCell component="th" size="medium">
              Account
            </TableCell>
            <TableCell component="th" size="medium">
              Entitlement
            </TableCell>
            <TableCell component="th" size="medium">
              Risk Score
            </TableCell>
            <TableCell component="th" size="medium">
              Identities
            </TableCell>
            <TableCell component="th" size="medium">
              <Tooltip
                title="Apono scans 90 days of activity to determine if an identity has been used recently."
                arrow
                placement="top"
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="subtitle2">Last used</Typography>
                  <MaterialIcon symbol="info" size="small" weight={500} />
                </Stack>
              </Tooltip>
            </TableCell>
            <TableCell component="th" size="medium" width="220">
              {remediationTitle}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
      </MuiTable>
    </TableWrapper>
  );
}

interface AssessmentReportTableItemProps {
  item: StandingAccessReportEntitlementFragmentFragment;
  onClick: () => void;
  selected?: boolean;
  remediationProgress?: ReactNode;
}

export function AssessmentReportTableItem({
  item,
  onClick,
  selected,
  remediationProgress,
}: AssessmentReportTableItemProps) {
  const permission = getFragmentData(standingAccessReportPermissionFragmentDocument, item.permission);
  const resource = getFragmentData(standingAccessReportResourceFragmentDocument, item.resource);

  return (
    <TableRow hover className="MuiTableRow-clickable" onClick={onClick} selected={selected}>
      <TableCell component="th" size="medium">
        <PrimarySecondary icon={resource?.type.icons.svg} primary={resource?.name} />
      </TableCell>
      <TableCell component="th" size="medium">
        {permission?.name}
      </TableCell>
      <TableCell component="th" size="medium">
        <RiskScoreItem riskScore={item.riskScore} maxRiskScore={9} withRiskScore />
      </TableCell>
      <TableCell component="th" size="medium">
        {item.identitiesCount} Users
      </TableCell>
      <TableCell component="th" size="medium">
        <AssessmentReportLastUsed lastUsedAt={item.lastUsedAt} />
      </TableCell>
      <TableCell component="th" size="medium">
        {remediationProgress}
      </TableCell>
    </TableRow>
  );
}

export function AssessmentReportTableItemLoadingRow() {
  return (
    <TableRow>
      <TableCell>
        <Skeleton variant="text" width="100%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="100%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="100%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="100%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="100%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="100%" />
      </TableCell>
    </TableRow>
  );
}
